import React, { useState, useEffect } from 'react'
import { TextInput, Text, Button, MultiSelect, Popover } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDispatch } from 'react-redux'
import { ReactComponent as ChevronUp } from 'app/styles/chevronUp.svg'
import { ReactComponent as ChevronDown } from 'app/styles/chevronDown.svg'
import { saveNote, updateNote, loadNotesByContract } from 'app/state/ducks/notes/thunks'
import { ContractType } from 'app/models/common-types'
import { Note } from 'app/models/note-params'
import { SuccessNotification, ErrorNotification } from 'app/views/components/notifications/notification'
import SelectedItemBadge from '../inputs/selects/selected-item-badge'
import MultiSelectOptionItem from '../inputs/selects/multi-select-option-item'
import RichTextEditorInput from 'app/views/components/rich-text-editor-input/rich-text-editor-input'
import 'app/styles/components/notes.css'



interface Props {
    contractType: ContractType
    contract: any
    selectedNote: Note | null
    setSelectedNote: (note: Note | null) => void
    isViewOnly: boolean
    setIsViewOnly: (isViewOnly: boolean) => void
}

export default function NotesForm({ contractType, contract, selectedNote, setSelectedNote, isViewOnly, setIsViewOnly }: Props) {
    const dispatch = useDispatch()
    const defaultTags = ['Deal', 'Facility', 'Loans', 'Borrowers', 'Lenders', 'Interest', 'Fees', 'Non Standard', 'Covenants', 'Other']
    // State to manage custom tags
    const [tags, setTags] = useState(defaultTags) // Track available tags
    const [customTag, setCustomTag] = useState('') // Track input value
    const [showPopover, setShowPopover] = useState(false) // Toggle popover

    const getAdmin = () => {
        if (contractType === ContractType.Deals) {
            return contract?.adminEntity?.[0]?.admin || ''
        }
        if (contractType === ContractType.Facilities) {
            return contract?.adminEntity?.admin || ''
        }
        if (contractType === ContractType.Entities) {
            return contract?.maker || ''
        }
        return ''
    }

    const form: any = useForm({
        initialValues: {
            id: selectedNote?.id || '',
            title: selectedNote?.title || '',
            tags: selectedNote?.tags || [],
            text: selectedNote?.text || '',
            author: 'John Doe',
            contractType: contractType,
            contractId: { id: contract.id, admin: getAdmin() }
        },
        validate: {
            title: (value: string) => (value.trim() === '' ? 'Note Title is required' : null),
            tags: (value: string[]) => (value.length === 0 ? 'At least one tag is required' : null),
            text: (value: string) => (value.trim() === '' || value === '<p></p>' ? 'Note Text is required' : null),
        },
        validateInputOnBlur: true,
        validateInputOnChange: true,
    })

    // Update form values when selectedNote changes
    useEffect(() => {
        if (!selectedNote) {
            form.reset() // Fully resets the form when no note is selected
            setTags(defaultTags)
        } else {
            form.setValues({
                id: selectedNote.id,
                title: selectedNote.title,
                tags: selectedNote.tags || [],
                text: selectedNote.text || '',
                author: 'John Doe',
                contractType: contractType,
                contractId: { id: contract.id, admin: getAdmin() },
            })
            setTags((prevTags) => {
                const uniqueTags = new Set([...prevTags, ...(selectedNote.tags || [])]);
                return Array.from(uniqueTags);
            })
        }
    }, [selectedNote]) // Runs when `selectedNote` changes

    useEffect(() => {
        const hasOther = form.values.tags.includes('Other')
        // Show the popover if "Other" is selected
        setShowPopover(hasOther)
    }, [form.values.tags])

    const handleCreateNote = async (e: React.FormEvent) => {
        e.preventDefault()

        const validationErrors = form.validate()
        if (validationErrors.hasErrors) return

        const noteData = {
            ...form.values,
            contractType: ContractType[form.values.contractType as keyof typeof ContractType],
        }

        try {
            const result = await dispatch(saveNote(noteData) as any)
            if (result.success) {
                SuccessNotification({
                    message: 'Note has been successfully saved.',
                    title: 'Save Successful',
                })
                form.reset()
            } else {
                ErrorNotification({ message: result.payload, title: 'Save note failed' })
            }
        } catch (error) {
            ErrorNotification({ message: `An unexpected error occurred: ${error}`, title: 'Error' })
        }
    }

    const handleUpdateNote = async (e: React.FormEvent) => {
        e.preventDefault()

        const validationErrors = form.validate()
        if (validationErrors.hasErrors) return

        const noteData = {
            ...form.values,
            contractType: ContractType[form.values.contractType as keyof typeof ContractType],
        }

        const { id, title, tags, text } = noteData

        try {
            const result = await dispatch(updateNote(id, text, tags, title) as any)
            if (result.success) {
                SuccessNotification({
                    message: 'Note has been successfully updated.',
                    title: 'Edit Successful',
                })
                // Reload notes list
                dispatch(loadNotesByContract(contract.id, contractType))
                form.reset()
            } else {
                ErrorNotification({ message: result.payload, title: 'Edit note failed' })
            }
        } catch (error) {
            ErrorNotification({ message: `An unexpected error occurred: ${error}`, title: 'Error' })
        }
    }

    const handleCancel = () => {
        form.reset()
        setSelectedNote(null)
        setIsViewOnly(false)
    }

    const handleCustomTagSave = () => {
        if (customTag.trim() && !tags.includes(customTag)) {
            const updatedTags = [...form.values.tags.filter((tag: string) => tag !== 'Other'), customTag]
            form.setFieldValue('tags', updatedTags)
            setTags(prevTags => [...prevTags, customTag])
            setShowPopover(false)
            setCustomTag('')
        }
    }

    // Custom valueComponent to filter out "Other"
    const CustomValueBadge = (props: any) => {
        if (props.value === 'Other') return null // Don’t render "Other" as a badge
        return <SelectedItemBadge {...props} />
    }

    return (
        <div className='notes-form-wrapper'>
            <form className='notes-form'>
                <>
                    <TextInput
                        w='100%'
                        label=''
                        variant='unstyled'
                        placeholder='Note Title'
                        className='notes-form-input'
                        disabled={isViewOnly}
                        {...form.getInputProps('title', { withError: false })}
                    />
                    {form.errors.title && (
                        <Text color="red" size="sm" mt='-10px' pl='1px'>
                            {form.errors.title}
                        </Text>
                    )}
                </>
                <Popover
                    opened={showPopover}
                    onClose={() => setShowPopover(false)}
                    position="bottom-start"
                    keepMounted
                >
                    <Popover.Target>
                        <MultiSelect
                            w='100%'
                            label=''
                            data={tags}
                            placeholder='Add Tags'
                            className='notes-form-multiselect'
                            searchable
                            multiple
                            clearable
                            variant='unstyled'
                            disabled={isViewOnly}
                            optionComponent={MultiSelectOptionItem}
                            valueComponent={CustomValueBadge}
                            {...form.getInputProps('tags', { withError: false })}
                            rightSection={
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                    <ChevronUp />
                                    <ChevronDown />
                                </div>
                            }
                            rightSectionWidth={28}
                            styles={{ rightSection: { pointerEvents: 'none' } }}
                        />
                    </Popover.Target>

                    {/* Popover Content for Custom Tag Input */}
                    <Popover.Dropdown w={664}>
                        <div className='notes-form-popover-wrapper'>
                            <div className='notes-form-popover-fields'>
                                <label className='notes-form-popover-label'>Other Tag</label>
                                <TextInput
                                    label=""
                                    placeholder="Custom Tag"
                                    value={customTag}
                                    onChange={(e) => setCustomTag(e.currentTarget.value)}
                                    className='notes-form-popover-input'
                                />

                                <Button onClick={handleCustomTagSave} mt="sm" className='notes-form-popover-button'>Save</Button>
                            </div>
                        </div>
                    </Popover.Dropdown>
                </Popover>
                {form.errors.tags && (
                    <Text color="red" size="sm" mt='-10px' pl='1px'>
                        {form.errors.title}
                    </Text>
                )}
                <div className='notes-form-group'>
                    <RichTextEditorInput
                        form={form}
                        fieldName="text"
                        placeholder="Write a description here or post an update..."
                        isDisabled={isViewOnly}
                        isMandatory={true}
                    />
                </div>
                {form.errors.text && (
                    <Text color="red" size="sm" pl='2px' mt='-10px' mb='-10px'>
                        {form.errors.text}
                    </Text>
                )}
            </form>
            <div className='notes-form-buttons'>
                <Button variant="outline" className='notes-form-button cancel' onClick={handleCancel}>Cancel</Button>
                {!isViewOnly && <Button className='notes-form-button save' onClick={selectedNote ? handleUpdateNote : handleCreateNote}>{selectedNote ? 'Save Edits' : 'Save Note'}</Button>}
                {isViewOnly && <Button className='notes-form-button save' onClick={() => setIsViewOnly(false)}>Edit</Button>}
            </div>
        </div>
    )
}
